import type { UserApp } from 'src/types/user';
import type { RoleEnum, Pagination, ValidationRuleDTO } from 'src/types/common';
import type { AppConfig, AppConfigRule, UserAppConfig, AppCollaborator, AppCustomerAttributeField } from 'src/types/app';

import { enqueueSnackbar } from 'notistack';

import { ToastOptions } from 'src/app-configs';
import { appsModel } from 'src/store/appsModel';
import { configModel } from 'src/store/configModel';

import api from './api';
import { LoadingWrapper } from './loading-wrapper';

export const apis = {
  createApp: async ({ name, description }: { name: string; description: string }) => {
    try {
      await api.post('/apps', { name, description });
      apis.getAppsByUser();
      enqueueSnackbar('App created succesfully!', {
        variant: 'success',
        ...ToastOptions,
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error creating app', {
        variant: 'error',
        ...ToastOptions,
      });
      console.log('createApp', error);
    }
  },
  updateApp: async (appId: number | string, data: { name?: string; description?: string; attributionWindow?: number }) => {
    try {
      await api.patch(`/apps/${appId}`, data);
      apis.getAppsByUser();
      enqueueSnackbar('App updated successfully', {
        variant: 'success',
        ...ToastOptions,
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error updating app', {
        variant: 'error',
        ...ToastOptions,
      });
      console.log('createApp', error);
    }
  },
  deleteApp: async (id: number) => {
    try {
      await api.delete(`/apps/${id}`);
      apis.getAppsByUser();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error deleting app');
      console.log('deleteApp', error);
    }
  },
  // eslint-disable-next-line arrow-body-style
  addCollaborator: async (id: number | string, data: { email: string; role: RoleEnum }) => {
    try {
      await api.post(`/apps/${id}/invite`, data);
      enqueueSnackbar('Collaborator added successfully', {
        variant: 'success',
        ...ToastOptions,
      });
      apis.getAppCollaborators(id);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error adding collaborator', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  removeCollaborator: async (id: number | string, data: { email: string }) => {
    try {
      await api.delete(`/apps/${id}/collaborators`, { data });
      enqueueSnackbar('Collaborator removed successfully', {
        variant: 'success',
        ...ToastOptions,
      });
      apis.getAppCollaborators(id);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error removing collaborator', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  updateCollaboratorRole: async (id: number | string, data: { email: string; role: RoleEnum }) => {
    try {
      await api.patch(`/apps/${id}/collaborators`, data);
      enqueueSnackbar('Collaborator role updated successfully', {
        variant: 'success',
        ...ToastOptions,
      });
      apis.getAppCollaborators(id);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error updating collaborator role', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  updateConfigPublishStatus: async (appId: number | string, data: { published: boolean }) => {
    try {
      await api.patch(`/apps/${appId}/publish`, data);
      apis.getAppById(appId);
      enqueueSnackbar('App publish status updated', {
        variant: 'success',
        ...ToastOptions,
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error updating app status', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  getAppsByUser: async () => {
    const { data } = await api.get<Pagination<UserApp>>('/apps').catch();
    if (!data) {
      return;
    }
    appsModel.setState({ apps: data });
  },
  getAppById: async (id: number | string) => {
    try {
      const { data } = await api.get<UserAppConfig>(`/apps/${id}`);
      appsModel.setState({ currentApp: data });
      configModel.setState({
        currentConfig: data?.appConfigs.find((c: AppConfig) => c?.isCurrent),
      });
      apis.getAppConfigRule(id);
      return data;
    } catch (error) {
      console.log('getAppById', error);
      return null;
    }
  },
  getAppConfigRule: async (id: number | string) => {
    try {
      const { data } = await api.get<AppConfigRule>(`/apps/${id}/config-rules`);
      configModel.setState({
        configRules: data
      });
      return data?.configRules || null;
    } catch (error) {
      console.log('getAppConfigRule', error);
      return null;
    }
  },
  getAppCollaborators: async (id: number | string, query = { page: 0, limit: 10 }) => {
    try {
      // @ts-ignore
      const q = new URLSearchParams(query).toString();
      const { data } = await api.get<Pagination<AppCollaborator>>(`/apps/${id}/collaborators?${q}`);
      appsModel.setState({ collaborators: data });
    } catch (error) {
      console.log('getAppCollaborators', error);
    }
  },
  getAppConfigs: async (id: number | string, query = { limit: 5, page: 0 }) => {
    try {
      // @ts-ignore
      const q = new URLSearchParams(query).toString();
      const { data } = await api.get<Pagination<AppConfig>>(`/apps/${id}/configs?${q}`);
      configModel.setState({ configs: data });
    } catch (error) {
      console.log('getAppConfigs', error);
    }
  },
  updateAppConfig: async (appId: number | string, data: Record<string, any>) =>
    api.patch(`/apps/${appId}/config/publish-new`, data),
  updateValidationRule: async (appId: number | string, data: ValidationRuleDTO) => {
    try {
      await api.patch(`/apps/${appId}/rules`, data);
      apis.getAppById(appId);
      enqueueSnackbar('Rule saved successfully!', {
        variant: 'success',
        ...ToastOptions,
      });
      return true;
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message ?? 'Error saving rule', {
        variant: 'error',
        ...ToastOptions,
      });
      return false;
    }
  },
  deleteValidationRule: async (appId: number | string, data: { field: string }) => {
    try {
      await api.patch(`/apps/${appId}/rules/delete`, data);
      apis.getAppById(appId);
      enqueueSnackbar('Validation rule deleted successfully', {
        variant: 'success',
        ...ToastOptions,
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error deleting validation rule', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  generateApiKey: async (appId: number | string) => {
    try {
      const { data } = await api.post(`/apps/${appId}/api-key`);
      return data;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error generating API Key', {
        variant: 'error',
        ...ToastOptions,
      });
      return null;
    }
  },
  getAppUsage: async (appId: number | string) => {
    try {
      const { data } = await api.get(`/usage/${appId}`);
      return data;
    } catch (error) {
      return [];
    }
  },
  rollbackAppConfig: async (appId: number | string, version: number | string) => {
    try {
      await api.post(`/apps/${appId}/rollback/${version}`, { reason: '', appId, version });
      enqueueSnackbar('App configuration rolled back successfully', {
        variant: 'success',
        ...ToastOptions,
      });
      apis.getAppById(appId);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error rolling back app configuration', {
        variant: 'error',
        ...ToastOptions,
      });
    }
  },
  getAppCustomerAttributeFields: async (appId: number) => {
    try {
      const response = await api.get<AppCustomerAttributeField[]>(`apps/${appId}/attributes`);
      return response.data;
    } catch (error) {
      console.log('getAppCustomerAttributeFields', error);
      return [];
    }
  },
};

export const AppsApi = LoadingWrapper(apis, appsModel);
